import { Filters } from "@/types/filters";
import { cn } from "@/utils/cn";
import { getNameFromCode } from "@/utils/get-name-from-code";
import {
	ArrowUpDown,
	Banknote,
	Bath,
	BedDouble,
	Building,
	Car,
	ChevronDown,
	ChevronUp,
	Clock,
	Compass,
	Droplets,
	FileText,
	Grid3x3,
	Hammer,
	Key,
	Layers,
	LayoutGrid,
	Maximize,
	Ruler,
	Search,
	Sofa,
	Sparkles,
	Wallet,
	X,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";

// Import individual filter components
import { Button } from "@/components/rac/button";
import { BalconiesSection } from "./sections/balconies-section";
import { BathroomsSection } from "./sections/bathrooms-section";
import { BedroomsSection } from "./sections/bedrooms-section";
import { DynamicAreaSection } from "./sections/dynamic-area-section";
import { FloorsSection } from "./sections/floors-section";
import { MaxAgeSection } from "./sections/max-age-section";
import { ParkingsSection } from "./sections/parkings-section";
import { PossessionSection } from "./sections/possession-section";
import { RentalValueSection } from "./sections/rental-value-section";

// Re-export types from the main filters module
export type { Filters, PropertyType, RangeValues } from "@/types/filters";

import { PlaceAutocompleteWrapper } from "@/components/others/place-autocomplete-wrapper";
import { AmenitiesIconGrid } from "@/components/rhf/amenities-icon-grid";
import { BudgetField as BudgetNew } from "@/components/rhf/budget-field";
import { Input } from "@/components/rhf/input";
import { PropertyTypeField } from "@/components/rhf/property-type-field";
import { useUrlFilters } from "@/hooks/use-url-filters";
import facingOptions from "@/info/facings";
import furnishingOptions from "@/info/furnishing-types";
import { documentsOptions } from "@/info/selectOptions/documents";
import { waterSourcesShortOptions } from "@/info/selectOptions/waterSourcesShort";
import { spaces as spacesOptions } from "@/info/spaces";
import { constructionStatus as constructionStatusOptions } from "@/info/unit/constructionStatus";
import levelOptions from "@/info/unit/levels";
import { propertyTypeOptions } from "@/info/unit/property-types";
import { MapProvider } from "@/providers/map";
import { RangeValues } from "@/types/filters";
import formikOptions from "@/utils/formikOptions";
import {
	handlePropertyTypeChange,
	handleStatusChange,
	isMaxAgeDisabled,
	isPlotFacingDisabled,
	isPossessionDisabled,
	createFormDefaults as sharedCreateFormDefaults,
} from "./shared-utils";
import { useFilterHandlers } from "./use-filter-handlers";

// Selected Filters Chips Component
function SelectedFilters({
	filters,
	onClearFilter,
}: {
	filters: Partial<Filters>;
	onClearFilter: (key: keyof Filters) => void;
}) {
	// Define valid user-facing filter keys that should be displayed
	const validFilterKeys = new Set([
		"locations",
		"propertyType",
		"bedrooms",
		"bathroomsMin",
		"budget",
		"builtupAreaMin",
		"floors",
		"balconiesMin",
		"parkingsMin",
		"rentalValue",
		"facing",
		"furnishing",
		"status",
		"amenities",
		"documents",
		"spaces",
		"waterSources",
		"levelType",
		"maxAge",
		"possession",
		"plotFacing",
	]);

	const activeFilters = Object.entries(filters).filter(([key, value]) => {
		// Only show valid user-facing filter keys
		if (!validFilterKeys.has(key)) return false;
		if (key === "bounds") return false;
		if (Array.isArray(value)) return value.length > 0;
		if (typeof value === "object" && value !== null) {
			if ("min" in value && "max" in value) {
				const rangeValue = value as RangeValues;
				return (rangeValue?.min ?? 0) > 0 || (rangeValue?.max ?? 0) > 0;
			}
			return Object.keys(value).length > 0;
		}
		if (typeof value === "string") return value;
		if (typeof value === "number") return value > 0;
		return false;
	});

	if (activeFilters.length === 0) return null;

	// UI: match filter UI, but with slight differentiation
	// Map filter keys to their options arrays for label lookup
	const optionsMap: Record<string, { code: string; name: string }[]> = {
		spaces: spacesOptions,
		facing: facingOptions,
		levelType: levelOptions,
		plotFacing: facingOptions,
		documents: documentsOptions,
		furnishing: furnishingOptions,
		waterSources: waterSourcesShortOptions,
		status: constructionStatusOptions.map(opt => ({ code: opt.value, name: opt.label })),
		propertyType: propertyTypeOptions.map(opt => ({ code: opt.value, name: opt.label })),
		// Add more mappings as needed
	};

	return (
		<div className="p-4 border-b bg-primary-50/40" id="selected-filters">
			<h3 className="text-base font-bold text-primary-700 mb-2">Selected Filters</h3>
			<div className="flex flex-wrap gap-2">
				{activeFilters.map(([key, value]) => {
					// Sentence case for label
					const labelKey = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, " $1");
					let label = "";
					const options = optionsMap[key] || [];
					if (Array.isArray(value)) {
						// Get names and deduplicate by name (case-insensitive) while preserving original case
						const names = value
							.map(v => {
								if (typeof v === "string") return options.length ? getNameFromCode(options, v) || v : v;
								if (typeof v === "object" && v && "name" in v && typeof v.name === "string") return v.name;
								return "";
							})
							.filter(Boolean);

						// Deduplicate names using Set
						const uniqueNames = Array.from(new Set(names));

						label = `${labelKey}: ${uniqueNames.join(", ")}`;
					} else if (typeof value === "object" && value !== null && "min" in value && "max" in value) {
						// Special formatting for budget values
						if (key === "budget") {
							const formatBudgetValue = (val: number) => {
								if (val >= 10000000) {
									// 1 Crore = 10000000
									return `${(val / 10000000).toFixed(1)}Cr`;
								} else if (val >= 100000) {
									// 1 Lakh = 100000
									return `${(val / 100000).toFixed(1)}L`;
								}
								return val.toString();
							};
							label = `${labelKey}: ${formatBudgetValue(value.min ?? 0)} - ${formatBudgetValue(value.max ?? 0)} L`;
						} else {
							label = `${labelKey}: ${value.min} - ${value.max}`;
						}
					} else if (typeof value === "string") {
						// Skip empty strings
						if (!value) return null;
						// Special formatting for age/possession string values
						if (key === "maxAge") {
							// Find the label from constructionStatus
							const readyToMoveOptions =
								constructionStatusOptions.find(s => s.value === "readyToMoveIn")?.options || [];
							const option = readyToMoveOptions.find(opt => opt.value === value);
							label = `${labelKey}: ${option?.label || value}`;
						} else if (key === "possession") {
							// Find the label from constructionStatus
							const underConstructionOptions =
								constructionStatusOptions.find(s => s.value === "underConstruction")?.options || [];
							const option = underConstructionOptions.find(opt => opt.value === value);
							label = `${labelKey}: ${option?.label || value}`;
						} else {
							label = `${labelKey}: ${options.length ? getNameFromCode(options, value) || value : value}`;
						}
					} else if (typeof value === "number") {
						label = `${labelKey}: ${value}`;
					} else {
						label = `${labelKey}: ${value}`;
					}

					return (
						<div
							key={key}
							className="flex items-center bg-primary-100 text-primary-800 rounded-full pl-3 pr-1 py-1 text-sm border border-primary-200 shadow-sm"
							onClick={e => e.stopPropagation()} // Prevent chip clicks from bubbling up
						>
							<span className="font-medium mr-1">{label}</span>
							<button
								type="button"
								aria-label={`Remove ${label} filter`}
								className="h-6 w-6 ml-1 text-primary-700 hover:text-primary-900 hover:bg-primary-200 rounded-full flex items-center justify-center transition-colors"
								onClick={e => {
									// Execute the clear filter function first
									onClearFilter(key as keyof Filters);
									// Then prevent event from bubbling up to modal close handlers
									e.preventDefault();
									e.stopPropagation();
								}}
								onMouseDown={e => {
									// Prevent mousedown from bubbling to prevent modal close
									e.stopPropagation();
								}}
							>
								<X className="h-4 w-4" />
							</button>
						</div>
					);
				})}
			</div>
		</div>
	);
}

// PropertyFiltersData is now deprecated - use Filters from @/types/filters instead

interface PropertyFiltersProps {
	onApplyFilters?: (data: Partial<Filters>) => void;
	onClearFilters?: (data: Partial<Filters>) => void;
	onClose?: () => void;
	onClear?: () => void;
	defaultValues?: Partial<Filters>;
	className?: string;
	isMobile?: boolean;
	onResetForm?: (resetFn: () => void) => void;
}

export interface PropertyFiltersRef {
	clearFilters: () => void;
}

interface FilterSectionProps {
	title: string;
	children: React.ReactNode;
	defaultOpen?: boolean;
	className?: string;
	disabled?: boolean;
	disabledReason?: string;
	allowOverflow?: boolean;
}

export function FilterSection({
	title,
	children,
	defaultOpen = true,
	className,
	disabled = false,
	disabledReason,
	allowOverflow = false,
}: FilterSectionProps) {
	const [isOpen, setIsOpen] = useState(defaultOpen);

	return (
		<div className={cn("bg-white rounded-lg border border-gray-200", !allowOverflow && "overflow-hidden", className)}>
			<button
				type="button"
				onClick={() => setIsOpen(!isOpen)}
				disabled={disabled}
				className={cn(
					"w-full px-4 py-4 flex items-center justify-between text-left font-medium text-gray-900 hover:bg-gray-50 transition-colors",
					disabled && "opacity-50 cursor-not-allowed",
				)}
			>
				<div className="flex flex-col">
					<span className="text-sm font-semibold">{title}</span>
					{disabled && disabledReason && <span className="text-xs text-gray-500 mt-1">{disabledReason}</span>}
				</div>
				{!disabled &&
					(isOpen ? (
						<ChevronUp className="w-5 h-5 text-gray-500" />
					) : (
						<ChevronDown className="w-5 h-5 text-gray-500" />
					))}
			</button>
			{isOpen && !disabled && <div className="px-4 pb-6">{children}</div>}
		</div>
	);
}

// Use shared createFormDefaults function
const createFormDefaults = sharedCreateFormDefaults;

export function DesktopFilters({
	onApplyFilters,
	onClose,
	defaultValues,
	className,
	isMobile,
	onResetForm,
}: PropertyFiltersProps) {
	// Get URL filters only for reading initial state and manual updates (no real-time sync)
	const { filters: urlFilters } = useUrlFilters();

	// Initialize form with URL filters once on mount
	const [initialFormValues] = useState(() => ({
		...createFormDefaults(),
		...defaultValues,
		...urlFilters, // Use URL filters as initial values only
	}));

	// TODO: Remove hack later - manual dirty field tracking
	const manualDirtyFields = useRef<Record<string, boolean>>({});

	// Reset signal for fields with internal state (like BudgetField)
	const [resetSignal, setResetSignal] = useState(false);

	// State to manage autoFocus on location filter - only focus on initial mount
	const [shouldFocusLocation, setShouldFocusLocation] = useState(true);

	const form = useForm<Filters>({
		defaultValues: initialFormValues, // Use local initial values
		mode: "onChange", // Enable onChange mode for better dirty detection
		shouldFocusError: false, // Prevent auto-focus on errors in modal
	});
	const { control, handleSubmit, setValue, reset } = form;

	// Disable autoFocus after initial mount to prevent focus stealing on re-renders
	useEffect(() => {
		if (shouldFocusLocation) {
			// Set a timeout to disable autoFocus after the initial focus has been applied
			const timer = setTimeout(() => {
				setShouldFocusLocation(false);
			}, 100);
			return () => clearTimeout(timer);
		}
	}, [shouldFocusLocation]);

	// Provide reset function to parent component
	useEffect(() => {
		if (onResetForm) {
			onResetForm(() => {
				// Set reset signal to true to bypass "active input" check in child components
				setResetSignal(true);
				// Reset form to default values
				const formDefaults = createFormDefaults();
				reset(formDefaults);
				// Clear manual dirty fields
				manualDirtyFields.current = {};
			});
		}
	}, [onResetForm, reset]);

	// Use the common filter handlers hook
	const { handleApplyFilters } = useFilterHandlers({
		form,
		manualDirtyFields,
		onApplyFilters,
	});

	// Only watch fields that are needed for logic or UI

	// Use useWatch for only the fields we care about to avoid unnecessary re-renders
	const watchedPropertyType = useWatch({ control, name: "propertyType" });
	const watchedStatus = useWatch({ control, name: "status" }) || "";
	const watchedBedrooms = useWatch({ control, name: "bedrooms" }) || { min: 0, max: 0 };
	const watchedBathroomsMin = useWatch({ control, name: "bathroomsMin" });
	const watchedBudget = useWatch({ control, name: "budget" });
	const watchedDocuments = useWatch({ control, name: "documents" });
	const watchedFacing = useWatch({ control, name: "facing" });
	const watchedFurnishing = useWatch({ control, name: "furnishing" });
	const watchedRentalValue = useWatch({ control, name: "rentalValue" });
	const watchedSpaces = useWatch({ control, name: "spaces" });
	const watchedWaterSources = useWatch({ control, name: "waterSources" });
	const watchedBuiltupAreaMin = useWatch({ control, name: "builtupAreaMin" });
	const watchedFloors = useWatch({ control, name: "floors" });
	const watchedBalconiesMin = useWatch({ control, name: "balconiesMin" });
	const watchedParkingsMin = useWatch({ control, name: "parkingsMin" });
	const watchedLevelType = useWatch({ control, name: "levelType" });
	const watchedAmenities = useWatch({ control, name: "amenities" });
	const watchedLocations = useWatch({ control, name: "locations" });
	const watchedMaxAge = useWatch({ control, name: "maxAge" });
	const watchedPossession = useWatch({ control, name: "possession" });
	const watchedPlotFacing = useWatch({ control, name: "plotFacing" });
	const watchedPlotAreaMin = useWatch({ control, name: "plotAreaMin" });
	const plotAreaMin = watchedPlotAreaMin ?? 0;

	// Function to mark fields as manually dirty (hack for broken React Hook Form dirty detection)
	// IMPORTANT: Memoize this function to prevent recreating it on every render
	// which would cause infinite loops in child components like BudgetField
	const markFieldDirty = useCallback((fieldName: string) => {
		manualDirtyFields.current[fieldName] = true;
	}, []);

	const watchedBedroomsMin = watchedBedrooms.min ?? 0;

	// Reconstruct watchedValues for SelectedFilters and filter section logic
	const watchedValues: Partial<Filters> = {
		propertyType: watchedPropertyType,
		status: watchedStatus,
		bedrooms: watchedBedrooms,
		bathroomsMin: watchedBathroomsMin,
		budget: watchedBudget,
		documents: watchedDocuments,
		facing: watchedFacing,
		furnishing: watchedFurnishing,
		rentalValue: watchedRentalValue,
		spaces: watchedSpaces,
		waterSources: watchedWaterSources,
		builtupAreaMin: watchedBuiltupAreaMin,
		floors: watchedFloors,
		balconiesMin: watchedBalconiesMin,
		parkingsMin: watchedParkingsMin,
		levelType: watchedLevelType,
		amenities: watchedAmenities,
		locations: watchedLocations,
		maxAge: watchedMaxAge,
		possession: watchedPossession,
		...(watchedPropertyType !== "apartment" && { plotFacing: watchedPlotFacing }),
		...(watchedPropertyType !== "apartment" && { plotAreaMin }),
	};

	// Helpers for safe access
	// Lint/formatting: helpers should be inside the component, but indented for clarity
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const getLength = (arr: any): number => (Array.isArray(arr) ? arr.length : 0);
	const getMin = (obj: { min?: number } | undefined): number => obj?.min ?? 0;
	const getMax = (obj: { max?: number } | undefined): number => obj?.max ?? 0;

	const [activeSection, setActiveSection] = useState("search");
	const scrollContainerRef = useRef<HTMLDivElement>(null);

	const handleClearFilter = (filterKey: keyof Filters) => {
		// Get the default value for this field from form defaults
		const formDefaults = createFormDefaults();
		const defaultValue = formDefaults[filterKey];

		// Mark as manually dirty so it gets processed during apply
		// This ensures cleared fields are included in the filteredValues for proper processing
		manualDirtyFields.current[filterKey] = true;

		// Use shouldValidate: true to ensure immediate re-render
		setValue(filterKey as keyof Filters, defaultValue, {
			shouldDirty: true,
			shouldValidate: true,
			shouldTouch: true,
		});
	};

	// Effect to dynamically update bathroom min based on bedroom min
	useEffect(() => {
		const minAllowedBaths = watchedBedroomsMin > 1 ? Math.floor(watchedBedroomsMin) - 1 : 1;
		const currentBaths = watchedValues.bathroomsMin ?? 0;
		if (currentBaths > 0 && currentBaths < minAllowedBaths) {
			setValue("bathroomsMin", minAllowedBaths, { shouldDirty: true });
		}
	}, [watchedBedroomsMin, watchedValues.bathroomsMin, setValue]);

	useEffect(() => {
		const handler = (e: MouseEvent) => {
			const dropdown = window.settlinDropdownRef;
			if (dropdown && e.target instanceof Node && dropdown.contains(e.target)) {
				e.stopPropagation();
				e.preventDefault();
				e.stopImmediatePropagation();
				return false;
			}
		};

		// Only handle mousedown to prevent dialog closing
		document.addEventListener("mousedown", handler, true);

		return () => {
			document.removeEventListener("mousedown", handler, true);
		};
	}, []);

	// Determine which sections should be disabled based on current selections
	// Facing/PlotFacing logic based on propertyType
	const isPlot = watchedPropertyType === "plot";

	// Facing: available for apartment, house, villa
	const isFacingDisabled = isPlot;
	// PlotFacing: available for house, villa, plot
	const plotFacingDisabled = watchedPropertyType ? isPlotFacingDisabled(watchedPropertyType) : false;

	// Reset plotFacing if propertyType is apartment
	useEffect(() => {
		handlePropertyTypeChange({
			propertyType: watchedPropertyType,
			plotAreaMin: watchedPlotAreaMin,
			setValue,
		});
	}, [watchedPropertyType, watchedPlotAreaMin, setValue]);

	// MaxAge and Possession should be handled based on status (use enum)
	useEffect(() => {
		handleStatusChange(watchedStatus, setValue);
	}, [watchedStatus, setValue]);

	const maxAgeDisabled = isMaxAgeDisabled(watchedStatus);
	const possessionDisabled = isPossessionDisabled(watchedStatus);
	const minAllowedBathrooms = watchedBedroomsMin > 1 ? Math.floor(watchedBedroomsMin) - 1 : 1;

	// Define filter sections
	// Extract all dependencies to variables for useMemo
	const amenitiesLength = getLength(watchedValues.amenities);
	const balconiesMin = watchedValues.balconiesMin ?? 0;
	const bathroomsMin = watchedValues.bathroomsMin ?? 0;
	const bedroomsMax = getMax(watchedValues.bedrooms);
	const bedroomsMin = getMin(watchedValues.bedrooms);
	const budgetMax = watchedValues.budget?.max ?? 0;
	const budgetMin = watchedValues.budget?.min ?? 0;
	const builtupAreaMin = watchedValues.builtupAreaMin ?? 0;
	const documentsLength = getLength(watchedValues.documents);
	const facingLength = getLength(watchedValues.facing);
	const floorsMax = watchedValues.floors?.max ?? 0;
	const floorsMin = watchedValues.floors?.min ?? 0;
	const furnishing = watchedValues.furnishing;
	const levelTypeLength = getLength(watchedValues.levelType);
	const locationsLength = getLength(watchedValues.locations);
	const parkingsMin = watchedValues.parkingsMin ?? 0;
	const propertyType = watchedValues.propertyType;
	const rentalValueMax = watchedValues.rentalValue?.max ?? 0;
	const rentalValueMin = watchedValues.rentalValue?.min ?? 0;
	const spacesLength = getLength(watchedValues.spaces);
	const statusLength = getLength(watchedValues.status);
	const waterSourcesLength = getLength(watchedValues.waterSources);

	const filterSections = useMemo(() => {
		const sections = [
			// Priority filters at the top
			{
				id: "search",
				title: "Search",
				fullTitle: "Search locality / projects",
				hasValue: locationsLength > 0,
				icon: Search,
			},
			{ id: "propertyType", title: "Property type", hasValue: propertyType !== "", icon: Building },
			{ id: "bedrooms", title: "Bedrooms", hasValue: bedroomsMin > 0 || bedroomsMax > 0, icon: BedDouble },
			{ id: "budget", title: "Budget", hasValue: budgetMin > 0 || budgetMax > 0, icon: Wallet },
			{
				id: "builtupArea",
				title: "Builtup area",
				fullTitle: "Builtup area (min.)",
				hasValue: builtupAreaMin > 0,
				icon: Ruler,
			},

			// Conditionally add Plot area only when property type is not apartment
			...(watchedPropertyType !== "apartment"
				? [
						{
							id: "plotArea",
							title: "Plot area",
							fullTitle: "Plot area (min.)",
							hasValue: plotAreaMin > 0,
							icon: Maximize,
						},
					]
				: []),

			// Divider (represented by a special section that will be handled in the UI)
			{ id: "divider", title: "divider", hasValue: false, icon: Search }, // Icon ignored for divider

			// Rest of the filters in alphabetical order
			{ id: "amenities", title: "Amenities", hasValue: amenitiesLength > 0, icon: Sparkles },
			{ id: "balconies", title: "Balconies", fullTitle: "Balconies (min.)", hasValue: balconiesMin > 0, icon: Grid3x3 },
			{ id: "bathrooms", title: "Bathrooms", fullTitle: "Bathrooms (min.)", hasValue: bathroomsMin > 0, icon: Bath },
			{ id: "documents", title: "Documents", hasValue: documentsLength > 0, icon: FileText },
			{ id: "facing", title: "Facing", hasValue: facingLength > 0, disabled: isFacingDisabled, icon: Compass },
			{ id: "floors", title: "Floors", hasValue: floorsMin > 0 || floorsMax > 0, icon: Layers },
			{ id: "furnishing", title: "Furnishing", hasValue: Boolean(furnishing), icon: Sofa },
			{ id: "levelType", title: "Level type", hasValue: levelTypeLength > 0, icon: ArrowUpDown },
			{ id: "maxAge", title: "Max age", hasValue: watchedMaxAge !== "", disabled: maxAgeDisabled, icon: Clock },
			{ id: "parkings", title: "Parkings", fullTitle: "Parkings (min.)", hasValue: parkingsMin > 0, icon: Car },
			{
				id: "plotFacing",
				title: "Plot Facing",
				hasValue: getLength(watchedValues.plotFacing) > 0,
				disabled: plotFacingDisabled,
				icon: Compass,
			},
			{
				id: "possession",
				title: "Possession",
				hasValue: watchedPossession !== "",
				disabled: possessionDisabled,
				icon: Key,
			},
			{
				id: "rentalValue",
				title: "Rental value",
				hasValue: rentalValueMin > 0 || rentalValueMax > 0,
				icon: Banknote,
			},
			{ id: "spaces", title: "Spaces", hasValue: spacesLength > 0, icon: LayoutGrid },
			{ id: "status", title: "Status", hasValue: statusLength > 0, icon: Hammer },
			{ id: "waterSources", title: "Water sources", hasValue: waterSourcesLength > 0, icon: Droplets },
		];

		return sections;
	}, [
		locationsLength,
		propertyType,
		bedroomsMin,
		bedroomsMax,
		budgetMin,
		budgetMax,
		builtupAreaMin,
		watchedPropertyType,
		plotAreaMin,
		amenitiesLength,
		balconiesMin,
		bathroomsMin,
		documentsLength,
		facingLength,
		isFacingDisabled,
		floorsMin,
		floorsMax,
		furnishing,
		levelTypeLength,
		watchedMaxAge,
		maxAgeDisabled,
		parkingsMin,
		watchedValues.plotFacing,
		plotFacingDisabled,
		watchedPossession,
		possessionDisabled,
		rentalValueMin,
		rentalValueMax,
		spacesLength,
		statusLength,
		waterSourcesLength,
	]);

	const scrollToSection = (sectionId: string) => {
		const element = document.getElementById(`filter-${sectionId}`);
		if (element && scrollContainerRef.current) {
			element.scrollIntoView({ behavior: "smooth", block: "start" });
			setActiveSection(sectionId);
		}
	};

	// Intersection Observer to track visible sections
	useEffect(() => {
		const container = scrollContainerRef.current;
		if (!container) return;

		const observer = new IntersectionObserver(
			entries => {
				const containerTop = container.getBoundingClientRect().top;

				const intersectingEntries = entries.filter(e => e.isIntersecting);
				if (intersectingEntries.length === 0) return;

				// Sort entries by their distance to the top of the container
				intersectingEntries.sort(
					(a, b) =>
						Math.abs(a.boundingClientRect.top - containerTop) - Math.abs(b.boundingClientRect.top - containerTop),
				);

				// The first entry is the closest
				if (intersectingEntries.length > 0) {
					const sectionId = intersectingEntries[0].target.id.replace("filter-", "");
					setActiveSection(sectionId);
				}
			},
			{
				root: container,
				threshold: 0.1, // A small threshold
				rootMargin: "0px 0px -80% 0px", // Tripwire is the top 20% of the container
			},
		);

		filterSections.forEach(section => {
			const element = document.getElementById(`filter-${section.id}`);
			if (element) observer.observe(element);
		});

		return () => observer.disconnect();
	}, [filterSections]);

	return (
		<MapProvider>
			<div className={cn("w-full max-w-md md:max-w-none bg-gray-50 flex flex-col", className)}>
				{/* Header with drag handle */}
				<div className="flex-shrink-0 p-4 border-b">
					{/* Drag handle */}
					{isMobile && <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4"></div>}
					<div className="flex items-center justify-between">
						<h2 className="text-xl font-bold text-gray-800">Filters</h2>
						<Button
							variant="ghost"
							size="icon"
							className="h-8 w-8 text-gray-600 hover:text-gray-800 hover:bg-gray-100"
							aria-label="Close filters"
							onPress={onClose}
						>
							<X className="h-5 w-5" />
						</Button>
					</div>
				</div>

				{/* Main Content */}
				<div className="flex-1 flex min-h-0">
					{/* Navigation Sidebar */}
					<div className="w-64 flex-shrink-0 bg-white border-r border-gray-200 overflow-y-auto overflow-x-hidden sticky top-0 self-start custom-scrollbar h-full">
						<div className="p-3">
							<nav className="space-y-1">
								{filterSections.map(section =>
									section.id === "divider" ? (
										<div key={section.id} className="border-t border-gray-200 my-2"></div>
									) : (
										<button
											type="button"
											aria-label={`Scroll to ${section.title}`}
											key={section.id}
											onClick={() => scrollToSection(section.id)}
											disabled={section.disabled}
											className={cn(
												"w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 relative group flex items-center overflow-hidden",
												activeSection === section.id
													? "bg-primary-50 text-primary-700 shadow-sm ring-1 ring-primary-100"
													: "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
												section.disabled && "opacity-50 cursor-not-allowed hover:bg-transparent",
											)}
										>
											<div className="flex items-center flex-1 min-w-0 pr-4">
												{/* Icon */}
												{section.icon && (
													<section.icon
														className={cn(
															"w-4 h-4 mr-3 flex-shrink-0 transition-colors",
															activeSection === section.id
																? "text-primary-600"
																: "text-gray-400 group-hover:text-gray-600",
														)}
													/>
												)}
												<span className="truncate">{section.title}</span>
											</div>

											{/* Active Indicator (Red Dot) */}
											{section.hasValue && (
												<div className="absolute right-2 top-1/2 -translate-y-1/2 w-2 h-2 bg-red-500 rounded-full shadow-sm ring-2 ring-white ml-2 flex-shrink-0"></div>
											)}
										</button>
									),
								)}
							</nav>
						</div>
					</div>

					{/* Filters Content */}
					<div className="flex-1 overflow-y-auto" ref={scrollContainerRef}>
						<form id="property-filters-form" onSubmit={handleSubmit(handleApplyFilters)} className="p-6 space-y-8">
							<SelectedFilters filters={watchedValues as Partial<Filters>} onClearFilter={handleClearFilter} />

							{/* Search Section */}
							<div id="filter-search">
								<FilterSection title="Search locality / projects" allowOverflow={true}>
									<Controller
										name="locations"
										control={control}
										render={({ field }) => (
											<PlaceAutocompleteWrapper
												className={cn(
													"w-full px-4 pb-2 border border-gray-300 rounded-lg",
													"focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500",
													"disabled:bg-gray-50",
													"placeholder-gray-400 text-sm",
													"disabled:cursor-not-allowed disabled:opacity-50 md:text-sm [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
												)}
												value={field.value || []}
												onChange={value => {
													// console.log("anchors", value);
													field.onChange(value);
													markFieldDirty("locations");
												}}
												showChips={true}
												autoFocus={shouldFocusLocation}
												filterField={true}
											/>
										)}
									/>
								</FilterSection>
							</div>

							{/* Property Type */}
							<div id="filter-propertyType">
								<FilterSection title="Property type">
									<PropertyTypeField
										options={propertyTypeOptions}
										control={control}
										exactPropertyTypeName="subType"
										setValue={setValue}
										exactTypeRequired
										hideLabel
										markFieldDirty={markFieldDirty}
									/>
								</FilterSection>
							</div>

							{/* Bedrooms */}
							<div id="filter-bedrooms">
								<FilterSection title="Bedrooms">
									<Controller
										name="bedrooms"
										control={control}
										render={({ field }) => (
											<BedroomsSection
												value={field.value}
												onChange={value => {
													field.onChange(value);
													markFieldDirty("bedrooms");
												}}
											/>
										)}
									/>
								</FilterSection>
							</div>

							{/* Budget */}
							<div id="filter-budget">
								<FilterSection title="Budget">
									<BudgetNew
										name="budget"
										control={control}
										markFieldDirty={markFieldDirty}
										resetSignal={resetSignal}
									/>
								</FilterSection>
							</div>

							{/* Built-up Area */}
							<div id="filter-builtupArea">
								<FilterSection title="Builtup area (min.)">
									<Controller
										name="builtupAreaMin"
										control={control}
										render={({ field }) => (
											<DynamicAreaSection
												{...field}
												onChange={value => {
													field.onChange(value);
													markFieldDirty("builtupAreaMin");
												}}
											/>
										)}
									/>
								</FilterSection>
							</div>

							{/* Plot area - only render when property type is not apartment */}
							{watchedPropertyType !== "apartment" && (
								<div id="filter-plotArea">
									<FilterSection title="Plot area (min.)">
										<Controller
											name="plotAreaMin"
											control={control}
											render={({ field }) => (
												<DynamicAreaSection
													{...field}
													value={field.value ?? 0}
													onChange={value => {
														field.onChange(value);
														markFieldDirty("plotAreaMin");
													}}
												/>
											)}
										/>
									</FilterSection>
								</div>
							)}

							{/* Divider */}
							<div id="filter-divider" className="border-t border-gray-300 my-8"></div>

							{/* Amenities */}
							<div id="filter-amenities">
								<FilterSection title="Amenities">
									<AmenitiesIconGrid hideLabel allowCustom={false} control={control} markFieldDirty={markFieldDirty} />
								</FilterSection>
							</div>

							{/* Balconies */}
							<div id="filter-balconies">
								<FilterSection title="Balconies (min.)">
									<Controller
										name="balconiesMin"
										control={control}
										render={({ field }) => (
											<BalconiesSection
												{...field}
												onChange={value => {
													field.onChange(value);
													markFieldDirty("balconiesMin");
												}}
											/>
										)}
									/>
								</FilterSection>
							</div>

							{/* Bathrooms */}
							<div id="filter-bathrooms">
								<FilterSection title="Bathrooms (min.)">
									<Controller
										name="bathroomsMin"
										control={control}
										render={({ field }) => (
											<BathroomsSection
												{...field}
												onChange={value => {
													field.onChange(value);
													markFieldDirty("bathroomsMin");
												}}
												minAllowed={minAllowedBathrooms}
											/>
										)}
									/>
								</FilterSection>
							</div>

							{/* Documents */}
							<div id="filter-documents">
								<FilterSection title="Documents">
									<Input
										control={control}
										name="documents"
										itemsClassName="grid grid-cols-2 gap-2"
										options={formikOptions(documentsOptions)}
										type="checkbox"
										markFieldDirty={markFieldDirty}
									/>
								</FilterSection>
							</div>

							{/* Facing */}
							<div id="filter-facing">
								<FilterSection title="Facing" disabled={isFacingDisabled} disabledReason="Not applicable for Plot">
									<Input
										control={control}
										name="facing"
										itemsClassName="grid grid-cols-4 gap-2"
										options={formikOptions(facingOptions)}
										type="checkbox"
										markFieldDirty={markFieldDirty}
									/>
								</FilterSection>
							</div>

							{/* Floors */}
							<div id="filter-floors">
								<FilterSection title="Floors">
									<Controller
										name="floors"
										control={control}
										render={({ field }) => (
											<FloorsSection
												value={field.value}
												onChange={value => {
													field.onChange(value);
													markFieldDirty("floors");
												}}
											/>
										)}
									/>
								</FilterSection>
							</div>

							{/* Furnishing */}
							<div id="filter-furnishing">
								<FilterSection title="Furnishing">
									<Input
										control={control}
										name="furnishing"
										itemsClassName="grid grid-cols-2 gap-2"
										options={formikOptions(furnishingOptions)}
										type="radio"
										markFieldDirty={markFieldDirty}
									/>
								</FilterSection>
							</div>

							{/* Level Type */}
							<div id="filter-levelType">
								<FilterSection title="Level type">
									<Input
										control={control}
										name="levelType"
										itemsClassName="grid grid-cols-3 gap-2"
										options={formikOptions(levelOptions)}
										type="checkbox"
										markFieldDirty={markFieldDirty}
									/>
								</FilterSection>
							</div>

							{/* Max Age */}
							<div id="filter-maxAge">
								<FilterSection
									title="Max age"
									disabled={maxAgeDisabled}
									disabledReason="Applicable on status 'READY TO MOVE IN'"
								>
									<Controller
										name="maxAge"
										control={control}
										render={({ field }) => (
											<MaxAgeSection
												value={field.value}
												onChange={value => {
													field.onChange(value);
													markFieldDirty("maxAge");
												}}
											/>
										)}
									/>
								</FilterSection>
							</div>

							{/* Parkings */}
							<div id="filter-parkings">
								<FilterSection title="Parkings (min.)">
									<Controller
										name="parkingsMin"
										control={control}
										render={({ field }) => (
											<ParkingsSection
												{...field}
												onChange={value => {
													field.onChange(value);
													markFieldDirty("parkingsMin");
												}}
											/>
										)}
									/>
								</FilterSection>
							</div>

							{/* Plot Facing */}
							<div id="filter-plotFacing">
								<FilterSection
									title="Plot Facing"
									disabled={plotFacingDisabled}
									disabledReason="Not applicable for Apartment"
								>
									<Input
										setValue={setValue}
										control={control}
										itemsClassName="grid grid-cols-4 gap-2"
										options={formikOptions(facingOptions)}
										name="plotFacing"
										type="checkbox"
										markFieldDirty={markFieldDirty}
									/>
								</FilterSection>
							</div>

							{/* Possession */}
							<div id="filter-possession">
								<FilterSection
									title="Possession"
									disabled={possessionDisabled}
									disabledReason="Applicable on status 'UNDER CONSTRUCTION'"
								>
									<Controller
										name="possession"
										control={control}
										render={({ field }) => (
											<PossessionSection
												value={field.value}
												onChange={value => {
													field.onChange(value);
													markFieldDirty("possession");
												}}
											/>
										)}
									/>
								</FilterSection>
							</div>

							{/* Rental Value */}
							<div id="filter-rentalValue">
								<FilterSection title="Rental value">
									<Controller
										name="rentalValue"
										control={control}
										render={({ field }) => (
											<RentalValueSection
												value={field.value}
												onChange={value => {
													field.onChange(value);
													markFieldDirty("rentalValue");
												}}
											/>
										)}
									/>
								</FilterSection>
							</div>

							{/* Spaces */}
							<div id="filter-spaces">
								<FilterSection title="Spaces">
									<Input
										control={control}
										name="spaces"
										itemsClassName="grid grid-cols-4 gap-2"
										options={formikOptions(spacesOptions)}
										type="checkbox"
										markFieldDirty={markFieldDirty}
									/>
								</FilterSection>
							</div>

							{/* Status */}
							<div id="filter-status">
								<FilterSection title="Status">
									<Input
										control={control}
										name="status"
										itemsClassName="grid grid-cols-3 gap-2"
										options={constructionStatusOptions}
										type="radio"
										markFieldDirty={markFieldDirty}
									/>
								</FilterSection>
							</div>

							{/* Water Sources */}
							<div id="filter-waterSources">
								<FilterSection title="Water sources">
									<Input
										setValue={setValue}
										control={control}
										itemsClassName="grid grid-cols-3 gap-2"
										options={formikOptions(waterSourcesShortOptions)}
										name="waterSources"
										type="checkbox"
										markFieldDirty={markFieldDirty}
									/>
								</FilterSection>
							</div>
						</form>
					</div>
				</div>
			</div>
		</MapProvider>
	);
}
